<template>
  <Card>
    <template #title="">
      <div class="p-d-flex p-justify-between">
        ประเภทหลักสูตร
        <div>
          <Button
            label="สร้างใหม่"
            icon="pi pi-plus"
            class="p-ml-2"
            @click="onNewCourseType"
          ></Button>
          <back-button class="p-ml-2" />
        </div>
      </div>
    </template>
    <template #content="">
      <div class="p-grid">
        <div class="p-col-12">
          <div class="p-grid">
            <div class="p-col-12 p-lg-4 p-md-6 p-fluid">
              <div class="p-inputgroup">
                <span class="p-inputgroup-addon">
                  <i class="pi pi-search"></i>
                </span>
                <InputText
                  v-model="searchString"
                  placeholder="ระบุคำค้น"
                ></InputText>
              </div>
            </div>
          </div>
        </div>
        <div class="p-col-12">
          <DataTable
            :paginator="true"
            :rows="10"
            :rowsPerPageOptions="[10, 20, 50]"
            :value="filteredCourseType"
            class="p-datatable-sm"
            ref="courseTypeTable"
            dataKey="id"
          >
            <template #empty>ไม่พบข้อมูล</template>
            <Column field="id" header="รหัส" :sortable="true"> </Column>
            <Column field="description" header="Description" :sortable="true">
            </Column>
            <Column
              headerStyle="width: 10rem;"
              bodyStyle="width:10rem; text-align: right"
            >
              <template #body="props">
                <Button
                  class="p-button-rounded p-button-success p-mr-2"
                  icon="pi pi-pencil"
                  @click="onEditCourseType(props.data)"
                ></Button>
                <Button
                  class="p-button-rounded p-button-warning"
                  icon="pi pi-trash"
                  @click="onDeleteCourseType(props.data)"
                ></Button>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </template>
  </Card>

  <Dialog v-model:visible="showDialog" :modal="true" class="p-fluid">
    <div class="p-field">
      <label for="courseTypeId">รหัส</label>
      <InputText
        maxlength="3"
        id="username"
        v-model.trim="courseType.id"
        required="true"
        :disabled="existCourseTypeId"
        autofocus
        :class="{ 'p-invalid': submitted && !courseType.id }"
      >
      </InputText>
      <small class="p-error" v-if="submitted && !courseType.id">
        โปรดกรอกรหัส
      </small>
      <small class="p-error" v-if="duplicateCourseTypeId"> รหัสซ้ำ </small>
    </div>
    <div class="p-field">
      <label for="name">Description</label>
      <InputText
        id="name"
        v-model="courseType.description"
        maxlength="50"
      ></InputText>
    </div>
    <template #footer>
      <Button
        icon="pi pi-check"
        label="ยืนยัน"
        class="p-button-success"
        @click="onSaveCourseType"
      ></Button>
      <Button
        icon="pi pi-times"
        label="ยกเลิก"
        class="p-button-danger p-ml-2"
        @click="onHideDialog"
      ></Button>
    </template>
  </Dialog>
  <Dialog
    v-model:visible="popupDeleteDialog"
    :style="{ width: '450px' }"
    header="Confirm"
    :modal="true"
  >
    <div class="confirmation-content">
      <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem" />
      <span v-if="courseType"
        >Are you sure you want to delete <b>{{ courseType.id }}</b
        >?</span
      >
    </div>
    <template #footer>
      <Button
        label="No"
        icon="pi pi-times"
        class="p-button-text"
        @click="popupDeleteDialog = false"
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        class="p-button-text"
        @click="handleDeleteCourseType"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { ref, onMounted, computed } from "vue";
import { usePlugin } from "@/plugins";
import { clone } from "ramda";
import {
  CourseType,
  GetCourseTypeResponse,
  UpsertCourseTypeRequest,
} from "@/types";
import Fuse from "fuse.js";
import BackButton from "@/components/BackButton.vue";
export default {
  name: "CourseType",
  components: { BackButton },
  setup() {
    const { success, error, axios, setLoading, padZero } = usePlugin();

    const courseTypeTable = ref(null);
    const courseTypes = ref<CourseType[]>([]);
    const courseType = ref<CourseType>({} as CourseType);
    const showDialog = ref(false);
    const popupDeleteDialog = ref(false);
    const searchString = ref("");
    const submitted = ref(false);
    const existCourseTypeId = ref(false);
    const duplicateCourseTypeId = ref(false);

    const filteredCourseType = computed(() => {
      if (!searchString.value) return courseTypes.value;
      const fuse = new Fuse(courseTypes.value, {
        keys: ["courseTypeId", "description"],
      });
      const searchResult = fuse.search(searchString.value);
      return searchResult.map((x) => x.item);
    });

    const loadCourseTypes = async () => {
      setLoading(true);
      try {
        const result = await axios.get<GetCourseTypeResponse>(
          "api/v1/services/courseType/get"
        );
        courseTypes.value = result.data.courseTypes ?? [];
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onNewCourseType = () => {
      courseType.value = {} as CourseType;
      showDialog.value = true;
      submitted.value = false;
      existCourseTypeId.value = false;
    };

    const onSaveCourseType = async () => {
      submitted.value = true;
      if (courseType.value.id && courseType.value.id.length > 0) {
        if (
          !existCourseTypeId.value &&
          courseTypes.value.find(
            (comp) => comp.id === padZero(courseType.value.id, 3)
          )
        ) {
          //Create courseType ID
          duplicateCourseTypeId.value = true;
          return;
        }
        setLoading(true);
        try {
          const result = await axios.post("api/v1/services/courseType/upsert", {
            courseTypes: [
              {
                id: padZero(courseType.value.id, 3),
                description: courseType.value.description,
              },
            ],
          } as UpsertCourseTypeRequest);
          await loadCourseTypes();
          success("บันทึกข้อมูลสำเร็จแล้ว");
          showDialog.value = false;
        } catch (e) {
          error(e);
        } finally {
          setLoading(false);
        }
      }
    };

    const onHideDialog = () => {
      showDialog.value = false;
      submitted.value = false;
      duplicateCourseTypeId.value = false;
    };

    const onEditCourseType = (source: CourseType) => {
      courseType.value = clone(source);
      showDialog.value = true;
      existCourseTypeId.value = true;
    };

    const onDeleteCourseType = (source: CourseType) => {
      courseType.value = clone(source);
      popupDeleteDialog.value = true;
    };

    const handleDeleteCourseType = async () => {
      setLoading(true);
      try {
        const result = await axios.delete("api/v1/services/courseType/delete", {
          data: {
            courseTypes: [courseType.value],
          } as UpsertCourseTypeRequest,
        });
        await loadCourseTypes();
        success("ลบข้อมูลสำเร็จแล้ว");
        popupDeleteDialog.value = false;
      } catch (e) {
        error(e);
        popupDeleteDialog.value = false;
      } finally {
        setLoading(false);
      }
      // }
    };

    onMounted(async () => {
      await loadCourseTypes();
    });
    return {
      courseTypeTable,
      courseType,
      courseTypes,
      filteredCourseType,
      showDialog,
      submitted,
      existCourseTypeId,
      duplicateCourseTypeId,
      popupDeleteDialog,
      searchString,
      onNewCourseType,
      onEditCourseType,
      onDeleteCourseType,
      onSaveCourseType,
      handleDeleteCourseType,
      onHideDialog,
    };
  },
};
</script>
